import { LitElement, html, unsafeCSS } from "lit";
import { property, query } from "lit/decorators.js";
import type { SupportItem } from "../../types/SupportItem.js";
import "../support-menu-slideout/SupportMenuSlideout.js";
import tailwindShared from "../../../styles/tailwind-shared.css?inline";
import styles from "./SupportMenuPanel.css?inline";

export class SupportMenuPanel extends LitElement {
  static readonly styles = [unsafeCSS(tailwindShared), unsafeCSS(styles)];

  @property({ type: Array }) items: SupportItem[] = [];
  @property() productContext = "unknown";
  @property({ type: Boolean, reflect: true }) open = false;

  // P16: stable reference via @query instead of shadowRoot.querySelector
  @query("support-menu-slideout") private _slideout!: HTMLElement;

  private _onDocClick!: (e: MouseEvent) => void;
  private _onKeydown!: (e: KeyboardEvent) => void;
  // Native Popover "toggle" fires on open/close. On close (Escape / click-outside /
  // close button), sync open=false and notify App.
  private _onSlideoutToggle = (e: Event): void => {
    const { newState } = e as Event & { newState: string };
    if (newState === "closed" && this.open) {
      this.open = false;
      this._emitClose();
    }
  };

  override connectedCallback(): void {
    super.connectedCallback();
    // Portal: move to document.body to escape Bridge Bar overflow clipping.
    // Guard required: in real browsers appendChild on an already-connected node
    // removes-then-reinserts it, firing disconnectedCallback → connectedCallback
    // infinitely. happy-dom happened to treat same-parent appendChild as a no-op,
    // masking the bug in unit tests.
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
    }

    // composedPath() pierces shadow DOM boundaries so clicks inside
    // support-menu-item shadow roots are recognised as "inside the panel".
    this._onDocClick = (e: MouseEvent) => {
      if (!this.open) return;
      const path = e.composedPath();
      if (
        path.some((n) => n === this || (n instanceof Node && this.contains(n)))
      )
        return;
      this._emitClose();
    };
    this._onKeydown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && this.open) {
        this._emitClose();
      }
    };
    document.addEventListener("click", this._onDocClick);
    this.addEventListener("keydown", this._onKeydown);
  }

  override firstUpdated(): void {
    // toggle doesn't bubble so must be added directly on the shadow child after first render
    this._slideout?.addEventListener("toggle", this._onSlideoutToggle);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener("click", this._onDocClick);
    this.removeEventListener("keydown", this._onKeydown);
    this._slideout?.removeEventListener("toggle", this._onSlideoutToggle);
    // P14: guard against double-removal — only remove if still a direct child of body
    if (document.body.contains(this) && this.parentElement === document.body) {
      document.body.removeChild(this);
    }
  }

  // ARGUS PR #164 (MAJOR): focus-on-open moved into SupportMenuSlideout itself
  // (right after showPopover() actually runs). Setting focus here raced Lit's
  // update scheduling — `?open=` on the child is only applied in the child's own
  // (later) update cycle, so this fired before showPopover(), while the popover
  // was still closed and .focus() was a no-op.
  // P3: removed — close-panel must only be emitted by _emitClose(), never by
  // updated(). The previous dispatch here caused double-emission on every close.

  private _emitClose(): void {
    this.open = false;
    this.dispatchEvent(
      new CustomEvent("close-panel", { bubbles: true, composed: true }),
    );
  }

  override render() {
    return html`
      <support-menu-slideout header="Support" ?open=${this.open}>
        <div class="flex flex-col gap-3 py-2">
          <slot></slot>
        </div>
      </support-menu-slideout>
    `;
  }
}

if (!customElements.get("support-menu-panel")) {
  customElements.define("support-menu-panel", SupportMenuPanel);
}
